import { CalculatorType, MARKETPLACE, PROMOTION_TYPE } from 'typings'
import { dayEnd } from 'ui'
import {
  MoreForLessPromotion,
  RegularPromotion,
  type BuyAndWinPromotion,
  type BuyTogetherPromotion,
  type Promotion,
  type PromotionBase,
} from './typings'
import { CampaignPromotion } from './typings/campaign'
import { ProgressiveDiscountPromotion } from './typings/progressiveDiscount'

export const DEFAULT_PROMOTION_TYPE = PROMOTION_TYPE.REGULAR

const EMPTY_PROMOTION_BASE = Object.freeze<PromotionBase>({
  id: undefined,
  type: DEFAULT_PROMOTION_TYPE,
  general: {
    name: '',
    active: true,
    description: '',
  },
  scheduling: {
    start: new Date(),
    end: dayEnd(new Date()),
    recurrence: undefined,
  },

  storefront: {
    highlight: false,
    additionalInformation: [],
  },
})

export const EMPTY_BUY_TOGETHER_PROMOTION = Object.freeze<BuyTogetherPromotion>(
  {
    ...EMPTY_PROMOTION_BASE,
    type: PROMOTION_TYPE.BUY_TOGETHER,
    buyTogether: {
      trigger: undefined,
      benefit: undefined,
    },
    restrictions: {
      maxUsage: undefined,
      maxUsagePerClient: undefined,
      accumulate: false,
      accumulateWithManualPrices: false,
      budget: undefined,
      sellers: undefined,
      salesChannels: undefined,
      origin: MARKETPLACE,
    },
    conditions: {},
  }
)

export const EMPTY_BUY_AND_WIN_PROMOTION = Object.freeze<BuyAndWinPromotion>({
  ...EMPTY_PROMOTION_BASE,
  type: PROMOTION_TYPE.BUY_AND_WIN,
  buyAndWin: {
    trigger: undefined,
    benefit: undefined,
  },
  restrictions: {
    maxUsage: undefined,
    maxUsagePerClient: undefined,
    accumulate: false,
    budget: undefined,
    sellers: undefined,
    salesChannels: undefined,
    origin: MARKETPLACE,
  },
  conditions: {},
})

export const EMPTY_MORE_FOR_LESS_PROMOTION =
  Object.freeze<MoreForLessPromotion>({
    ...EMPTY_PROMOTION_BASE,
    type: PROMOTION_TYPE.MORE_FOR_LESS,
    moreForLess: {
      trigger: undefined,
      benefit: undefined,
    },
    restrictions: {
      maxUsage: undefined,
      maxUsagePerClient: undefined,
      maxItemsPerCart: undefined,
      accumulate: false,
      accumulateWithManualPrices: false,
      budget: undefined,
      sellers: undefined,
      salesChannels: undefined,
      origin: MARKETPLACE,
    },
    conditions: {},
  })

export const EMPTY_PROGRESSIVE_DISCOUNT_PROMOTION =
  Object.freeze<ProgressiveDiscountPromotion>({
    ...EMPTY_PROMOTION_BASE,
    type: PROMOTION_TYPE.PROGRESSIVE,
    progressive: {
      trigger: undefined,
      benefit: undefined,
    },
    restrictions: {
      maxUsage: undefined,
      maxUsagePerClient: undefined,
      maxItemsPerCart: undefined,
      accumulateWithManualPrices: false,
      budget: undefined,
      sellers: undefined,
      salesChannels: undefined,
      origin: MARKETPLACE,
    },
    conditions: {},
  })

export const EMPTY_REGULAR_PROMOTION = Object.freeze<RegularPromotion>({
  ...EMPTY_PROMOTION_BASE,
  type: PROMOTION_TYPE.REGULAR,
  regular: {
    scope: undefined,
    benefit: undefined,
  },
  restrictions: {
    maxUsage: undefined,
    maxUsagePerClient: undefined,
    maxItemsPerCart: undefined,
    maxItemsPerAttribute: undefined,
    accumulate: false,
    accumulateWithManualPrices: false,
    budget: undefined,
    sellers: undefined,
    salesChannels: undefined,
    origin: MARKETPLACE,
  },
  conditions: {},
})

export const EMPTY_CAMPAIGN_PROMOTION = Object.freeze<CampaignPromotion>({
  ...EMPTY_PROMOTION_BASE,
  type: PROMOTION_TYPE.CAMPAIGN,
  campaign: {
    scope: undefined,
    benefit: undefined,
    audiences: undefined,
  },
  restrictions: {
    maxUsage: undefined,
    maxUsagePerClient: undefined,
    maxItemsPerCart: undefined,
    maxItemsPerAttribute: undefined,
    budget: undefined,
    accumulate: false,
    accumulateWithManualPrices: false,
    sellers: undefined,
    salesChannels: undefined,
    origin: MARKETPLACE,
  },
})

export const EMPTY_PROMOTION: Partial<Record<CalculatorType, Promotion>> = {
  [PROMOTION_TYPE.BUY_TOGETHER]: EMPTY_BUY_TOGETHER_PROMOTION,
  [PROMOTION_TYPE.BUY_AND_WIN]: EMPTY_BUY_AND_WIN_PROMOTION,
  [PROMOTION_TYPE.MORE_FOR_LESS]: EMPTY_MORE_FOR_LESS_PROMOTION,
  [PROMOTION_TYPE.PROGRESSIVE]: EMPTY_PROGRESSIVE_DISCOUNT_PROMOTION,
  [PROMOTION_TYPE.REGULAR]: EMPTY_REGULAR_PROMOTION,
  [PROMOTION_TYPE.CAMPAIGN]: EMPTY_CAMPAIGN_PROMOTION,
}
